import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Check } from 'lucide-react';

const LANGUAGES = [
  { code: 'en', label: 'English', flagCode: 'gb', short: 'EN' },
  { code: 'pt-BR', label: 'Português (Brasil)', flagCode: 'br', short: 'PT' },
];

// flagcdn.com serves small, sharp country flag PNGs. Public CDN, no auth required.
const flagSrc = (code) => `https://flagcdn.com/w40/${code}.png`;
const flagSrcRetina = (code) => `https://flagcdn.com/w80/${code}.png 2x`;

const Flag = ({ code, size = 16 }) => (
  <img
    src={flagSrc(code)}
    srcSet={flagSrcRetina(code)}
    alt=""
    width={size * 1.4}
    height={size}
    style={{ borderRadius: 2, display: 'block', objectFit: 'cover', flexShrink: 0 }}
  />
);

const LanguageSwitcher = ({ compact = false }) => {
  const { i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Resolve current language to one of our supported codes (handles "pt", "pt-PT", etc → "pt-BR").
  const current = LANGUAGES.find((l) => i18n.resolvedLanguage === l.code) || LANGUAGES[0];

  const change = (code) => {
    i18n.changeLanguage(code);
    setOpen(false);
  };

  return (
    <div ref={ref} style={{ position: 'relative', display: 'inline-block' }}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="btn btn-ghost btn-sm"
        aria-label="Change language"
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          padding: compact ? '4px 8px' : '6px 10px',
        }}
      >
        <Flag code={current.flagCode} size={14} />
        {!compact && <span style={{ fontSize: 13 }}>{current.short}</span>}
      </button>
      {open && (
        <div
          style={{
            position: 'absolute',
            top: '100%',
            right: 0,
            marginTop: 6,
            background: '#1E293B',
            border: '1px solid var(--border)',
            borderRadius: 'var(--radius-md)',
            boxShadow: '0 10px 30px rgba(0,0,0,0.6)',
            zIndex: 1000,
            minWidth: 220,
            overflow: 'hidden',
          }}
        >
          {LANGUAGES.map((lang) => (
            <button
              key={lang.code}
              type="button"
              onMouseDown={(e) => {
                e.preventDefault();
                change(lang.code);
              }}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                width: '100%',
                padding: '12px 14px',
                background: 'transparent',
                border: 'none',
                cursor: 'pointer',
                textAlign: 'left',
                color: 'var(--text-primary)',
                fontSize: 14,
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'rgba(255,255,255,0.06)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'transparent';
              }}
            >
              <Flag code={lang.flagCode} size={18} />
              <span style={{ flex: 1 }}>{lang.label}</span>
              {current.code === lang.code && <Check size={14} style={{ color: 'var(--accent)' }} />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default LanguageSwitcher;
